import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Column, Row, Spacing, withTheme } from '@dls/web';
import { PlanSelector, FlowStickyFooter } from '@singtel-web/widgets';
import { navigate } from 'gatsby';
import styled from 'styled-components';
import SEO from '../SEO';
import LoadingOverlay from '../LoadingOverlay';
import PageWrapper from './page-wrapper';
import NeedHelpSection from './NeedHelpSection';
import Footer from '../Footer/Footer';
import { api, btlActions, orderActions, planActions, userActions } from '@detox/actions';
import { navigation } from '../../middlewares/navigation-constants';
import { BTLOfferDetails, MobileBTLOffer } from '../../types/plan.types';
import useTranslate from '../../hooks/useTranslation';
import {
  createPayloadForCalculatePayable,
  flattenNodes,
  formatBTLPrice,
  formatServiceNumberForPxAddons,
  getBTLAddonCategoryList,
  getBTLLayoutModel,
  getBTLOfferAddons,
  getSelectedAddonBoidList,
  mergeBTLAddonsWithPricing
} from '../../helpers/common';
import { ProductDetailsWrapper } from '../../templates/phone/styles';
import { APP_TYPE_ANY, KeyValue } from '../../types/common.types';
import BTLAddonsCarousel from './BTLAddonsCarousel';
import { filterMainline, getPendingOrderParams } from '../../helpers/chooseYourNumberHelper';
import isSimOnlyPlan from '../../helpers/is-sim-only-plan';
import { ACTION_TYPES } from '../../constants/actions';
import { useABTesting } from '@wec-core/ab-testing';
import { AbFeatureNames } from '../../constants/ab-feature-names';
import { BTL_CONFIGS } from '../../constants/common';

const { eShopGetPxAddons, eShopPxAddonsCalculatePayable } = api.onlineApi;

const { isMobileService } = api.mcss.helpers;

interface RootState {
  user: {
    information?: {
      clientContext?: any;
      userDetails?: KeyValue;
    };
    cis?: { information?: KeyValue };
    products?: KeyValue;
    loading?: boolean;
  };
  order: {
    loading?: boolean;
    productOrder?: KeyValue;
    productOrderCreated?: boolean;
    hasProcessingOrder?: boolean;
  };
  addons: {
    isSkeletonLoading?: boolean;
    selectedAddons?: KeyValue;
    orderActionAttributes?: KeyValue;
    manuallyAddedAddons?: KeyValue;
    availableAddons?: KeyValue;
    btlAddon?: KeyValue;
  };
  promotions: {
    btlOfferData?: BTLOfferDetails;
    btlOfferLoading?: boolean;
    btlOfferError?: boolean;
    btlCampignId?: string;
    btlCampignErrorDetail?: KeyValue;
  };
  plan: KeyValue,
  product: KeyValue
}

const StyledPlanSelector = withTheme(styled.div`
    background-color: #F5F5F8;
`);

const BTLOfferDetailPage = props => {
  const { data, abTestingLineSelectionEnabled } = props;
  const { t } = useTranslate();
  const dispatch = useDispatch();

  const {
    userInformation,
    order,
    promotions,
    plan,
    product,
    user,
    addons
  } = useSelector(
    (state: RootState) => ({
      userInformation: state.user?.information,
      order: state.order,
      promotions: state.promotions,
      plan: state.plan,
      product: state.product,
      user: state.user,
      addons: state.addons
    })
  );
  const { selectedPlan } = plan;
  const [btlOfferDetail, setBTLOfferDetails] = useState<BTLOfferDetails>();
  const isLoggedIn = !!userInformation?.clientContext;
  const loadingOrder = order?.loading || promotions?.btlOfferLoading || addons?.isSkeletonLoading || user?.loading;
  const { allPlan, allAddon, allAddonGroup, allAddonPreTick } = data;
  const plans = useMemo(() => flattenNodes(allPlan), [allPlan]);
  const addonList = useMemo(() => flattenNodes(allAddon), [allAddon]);

  const {
    isFeatureAllowed: abTestPxAddonsFeatureEnabled,
    isPending: abTestApiPending,
    isSuccess: abTestApiSuccess
  } = useABTesting({
    featureName: AbFeatureNames.BTL_PX_ADDONS
  });


  const addonGroupInfoLookup = useMemo(
    () =>
      allAddonGroup.edges.reduce((accum, cur) => {
        accum[cur.node.displayName] = cur.node;
        return accum;
      }, {}),
    [allAddonGroup.edges]
  );

  const allAddonPreTickList = useMemo(
    () => (data ? flattenNodes(allAddonPreTick) : []),
    [allAddonPreTick, data]
  );

  const pretickBoIds = allAddonPreTickList
    .flatMap(addon => addon.boIds || [])
    .filter(boid => BTL_CONFIGS.CAMPIGN_ADDON_ID_LIST.includes(boid));

  const filteredBTLAddonsWithPretick = useMemo(() => {
    return addonList
      .filter(addon => BTL_CONFIGS.CAMPIGN_ADDON_ID_LIST.includes(addon.productSpecContainmentID))
      .map(addon => ({
        ...addon,
        isPretick: pretickBoIds.includes(addon.productSpecContainmentID)
      }));
  }, [addonList, pretickBoIds]);

  // Call getBTLOffer call to get the plan boid details
  useEffect(() => {
    if (isLoggedIn && promotions?.btlCampignId) {
      dispatch(btlActions.getBtlOffersWithEshop({
        btlCampaignId: promotions?.btlCampignId
      }));
    }
  }, [promotions?.btlCampignId, isLoggedIn]);

  // Once receive BTL offer data, set it to state and also map with aem plans and set selected plan
  useEffect(() => {
    if (!promotions?.btlOfferData) return;
    if (promotions?.btlOfferData?.customerDetails?.eligible) {
      setBTLOfferDetails(promotions?.btlOfferData);
      const offerDetail = promotions?.btlOfferData;
      const mobileOfferDetail: MobileBTLOffer[] = offerDetail?.mobile || [];
      const firstOffer = mobileOfferDetail[0];
      const offerBOID = firstOffer?.plan?.BOID;
      const matchedPlan = plans.find(
        (plan) => plan.basePriceSchemaId === offerBOID?.toString()
      );

      if (firstOffer?.vas && firstOffer?.vas.length > 0) {
        const btlAddonCategoryInfo = getBTLAddonCategoryList(firstOffer?.vas);
        const nonPxBoidList = btlAddonCategoryInfo?.uniqueNonPXAddonList.flatMap((vas) =>
          [vas.BOID, vas.discountBOID].filter(Boolean)
        );

        dispatch({
          type: ACTION_TYPES.ADDONS.BTL_ADDON_UPDATE,
          payload: {
            btlPxAddons: btlAddonCategoryInfo?.uniquePxAddonList,
            btlNonPxAddons: nonPxBoidList,
            addonData: serviceAddonList
          }
        });
      }

      if (matchedPlan) {
        dispatch(planActions.setSelectedPlan(matchedPlan));
      }
    } else {
      // to handle ineligible customer case or error case
      navigate(`/${navigation.BTL_ERROR_PAGE}`, { replace: true });
    }
  }, [promotions?.btlOfferData]);

  useEffect(() => {
    if (promotions?.btlOfferError) {
      // if you receive eshop error while getting btl offer details, navigate to error page
      navigate(`/${navigation.ERROR}`, { replace: true });
    } else if (promotions?.btlCampignErrorDetail && promotions?.btlCampignErrorDetail?.error) {
      navigate(`/${navigation.BTL_ERROR_PAGE}`, { replace: true });
    }
  }, [promotions?.btlOfferError, promotions?.btlCampignErrorDetail?.error]);

  useEffect(() => {
    if (promotions?.btlOfferData?.customerDetails?.eligible) {
      let pxAddonsEnabled = false;
      const getPxAddonsApi = async () => {
        try {
          dispatch({
            type: ACTION_TYPES.ADDONS.ADDONS_FETCH_LOADING
          })
          const btlOfferServiceNo = promotions?.btlOfferData?.customerDetails?.serviceLines;
          const serviceNumber = formatServiceNumberForPxAddons(btlOfferServiceNo[0]);

          const pxAddons = await eShopGetPxAddons(serviceNumber);

          const addonToUpdateCalculateCall = createPayloadForCalculatePayable(pxAddons.existingAddons, addons?.btlAddon?.btlPxAddons);
          const addonsPriceInfo = await eShopPxAddonsCalculatePayable({
            serviceNumber,
            addonsList: addonToUpdateCalculateCall
          });

          const mergedAddons = mergeBTLAddonsWithPricing(
            addons?.btlAddon?.btlPxAddons,
            addonsPriceInfo?.body?.data
          );

          dispatch({
            type: ACTION_TYPES.ADDONS.PX_ADDONS_FETCH_SUCCESS,
            payload: {
              pxAddons: mergedAddons,
              pxExistingAddons: pxAddons.existingAddons
            }
          });
        } catch (error) {
          dispatch({
            type: ACTION_TYPES.ADDONS.PX_ADDONS_RESET,
            payload: {
              pxAddons: null,
              pxExistingAddons: null,
              productOrderId: null
            }
          });
        }
      };
      if (!abTestApiPending) {
        pxAddonsEnabled = abTestApiSuccess && abTestPxAddonsFeatureEnabled;
        if (pxAddonsEnabled && addons?.btlAddon?.btlPxAddons?.length > 0) {
          getPxAddonsApi();
        }
      }
    }
  }, [
    abTestApiPending,
    abTestApiSuccess,
    addons?.btlAddon?.btlPxAddons
  ]);

  useEffect(() => {
    if (selectedPlan?.planId && promotions?.btlOfferData?.customerDetails?.eligible) {
      const options = {
        getMainLinesOnly: false,
        shouldFetchMainLines: isSimOnlyPlan(selectedPlan),
        isEshopLineSelection: abTestingLineSelectionEnabled
      };
      dispatch(userActions.getUserInfoAndProducts(
        abTestingLineSelectionEnabled ? filterMainline(plans) : isMobileService,
        options
      ));
    }
  }, [selectedPlan?.planId]);

  useEffect(() => {
    if (order?.hasProcessingOrder) {
      navigate(
        `/${navigation.INFO}${getPendingOrderParams(
          !!user.information?.userDetails?.loginId
        )}`
      );
      dispatch({ type: ACTION_TYPES.ORDER.RESET_PROCESSING_ORDER });
    }
  }, [order?.hasProcessingOrder]);

  useEffect(() => {
    dispatch({
      type: ACTION_TYPES.SETTINGS.SET_APP_CONFIG,
      payload: { skipAddons: true }
    });
    dispatch({
      type: ACTION_TYPES.SIMTYPE.SET_SIMTYPE,
      payload: 'noSIM'
    });
    // dispatch({
    //   type: ACTION_TYPES.ADDONS.BTL_ADDON_UPDATE,
    //   payload: {
    //     addonData: serviceAddonList
    //   }
    // });
  }, []);

  // Compute sticky cart content
  const stickyCartContent = useMemo(() => {
    const mobileOfferDetail: MobileBTLOffer[] = btlOfferDetail?.mobile || [];
    const firstOffer = mobileOfferDetail[0];
    const offerPrice = firstOffer?.price?.offerPrice;
    const planDetail = firstOffer?.plan;

    if (offerPrice && Number(offerPrice) > 0) {
      return [{
        label: `For ${planDetail?.contractDuration}`,
        text: formatBTLPrice({ price: offerPrice, suffix: '/mth' })
      }];
    }

    return [];
  }, [btlOfferDetail]);


  const proceedToShoppingCart = () => {
    const createBTLOrderToProceed = async () => {
      try {
        const { selectedProduct } = product;
        const bccUserProducts = user?.products;
        const btlOfferServiceNo = promotions?.btlOfferData?.customerDetails?.serviceLines;
        const service = bccUserProducts?.find(
          (product) => product?.serviceId === btlOfferServiceNo[0]
        );
        const selectedAddonBoids = getSelectedAddonBoidList(addons?.btlAddon?.addonData);
        const mobileOfferDetail: MobileBTLOffer[] = promotions?.btlOfferData?.mobile || [];
        const firstOffer = mobileOfferDetail[0];

        const vasBoidArray = [...(addons?.btlAddon?.btlNonPxAddons || []), firstOffer?.plan?.discountBOID, ...(selectedAddonBoids || [])].filter(Boolean);
        const uniqueFinalVasBoid = [...new Set(vasBoidArray)];

        await (dispatch(
          orderActions.createBTLOrder({
            selectedPlan,
            service,
            contact: user.information.clientContext.contact,
            cisDetails: user.cis.information,
            simpleProductSpecId: selectedProduct?.productId,
            monthlyTerm: selectedProduct?.monthlyTerm,
            preTickAddons: allAddonPreTickList,
            selectedProduct: selectedProduct?.groupId,
            btlVasIds: uniqueFinalVasBoid,
            campignId: promotions?.btlCampignId
          })
        ) as APP_TYPE_ANY);

        return navigate(`/${navigation.SHOPPING_CART_PAGE}`);
      } catch (e) {
        console.log('Error creating BTL Recontract Order:', e);
      }
    };

    createBTLOrderToProceed();
  };

  const handlePlanSelect = (plan) => {
    dispatch(planActions.setSelectedPlan(plan));
  };

  const planSelectorArgs = {
    selectedPlan: getBTLLayoutModel(selectedPlan, btlOfferDetail)[0],
    defaultImage: {
      src: btlOfferDetail?.mobile[0]?.displayItems?.imageURL,
      alt: 'Plan image'
    },
    planSelectorOptions: [getBTLLayoutModel(selectedPlan, btlOfferDetail)[0]],
    hideSelector: true,
    onPlanSelect: handlePlanSelect
  };

  const serviceAddonList = getBTLOfferAddons(filteredBTLAddonsWithPretick, addonGroupInfoLookup);

  return (
    <>
      <SEO title="BTL Offer Page" />
      {loadingOrder && <LoadingOverlay />}
      {isLoggedIn && (
        <>
          <ProductDetailsWrapper>
            {loadingOrder && <LoadingOverlay />}

            {btlOfferDetail && (
              <StyledPlanSelector>
                <Row>
                  <Column>
                    <Spacing bottom={3}>
                      <PlanSelector.Layout1 {...planSelectorArgs} />
                    </Spacing>
                  </Column>
                </Row>
              </StyledPlanSelector>
            )}

            <PageWrapper>
              <Spacing top={3} bottom={2}>
                <BTLAddonsCarousel serviceAddons={serviceAddonList} />
              </Spacing>
              <NeedHelpSection />
            </PageWrapper>
          </ProductDetailsWrapper>

          <Footer />
          {btlOfferDetail && stickyCartContent.length > 0 && (
            <FlowStickyFooter
              buttonText={t('ACTION_TEXT_PROCEED')}
              action={{
                text: t('ACTION_TEXT_PROCEED'),
                onClick: proceedToShoppingCart
              }}
              content={stickyCartContent}
            />
          )}
        </>
      )}
    </>
  );
};

export default BTLOfferDetailPage;